import { datadogLogs } from '@datadog/browser-logs';
import { wwwURL } from './headerNavLinks';

/**
 * Validate the email and send the subscription event to Iterable
 * @param email The email address to subscribe.
 */
export function handleIterableSubscription(email: string): void {
  if (!isValidEmail(email)) {
    return showFlash('alert-error', 'Please enter a valid email.');
  }

  sendSubscribeEventToStore(email);
}

/**
 * Check whether an email is valid.
 * @param email The email address.
 * @returns True if the email is valid.
 */
export function isValidEmail(email: string): boolean {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
}

/**
 * Removes the specified HTML element from the DOM.
 * @param element The HTML element to remove.
 */
function remove(element: HTMLElement | null): void {
  if (element && element.parentNode) {
    element.parentNode.removeChild(element);
  }
}

/**
 * Creates a DOM node from an HTML string.
 * @param htmlString A string containing HTML.
 * @returns The first HTMLElement created from the string.
 * @throws Error if the HTML string does not produce an HTMLElement.
 */
function createElementFromHTML(htmlString: string): HTMLElement {
  const div = document.createElement('div');
  div.innerHTML = htmlString.trim();
  const element = div.firstChild;
  if (element instanceof HTMLElement) {
    return element;
  }
  throw new Error('Invalid HTML string');
}

/**
 * Inserts an element after a specified sibling element.
 * @param el The element to insert.
 * @param sibling The sibling element after which to insert.
 */
function insertAfter(el: HTMLElement, sibling: HTMLElement | null): void {
  if (sibling && sibling.parentNode) {
    sibling.parentNode.insertBefore(el, sibling.nextElementSibling);
  }
}

/**
 * Displays a flash message.
 * @param classes The CSS classes to apply.
 * @param text The message text.
 */
export function showFlash(classes: string, text: string): void {
  const alertSection = document.querySelector(
    '#alert-flash',
  ) as HTMLElement | null;
  const ewHeader = document.querySelector('.ew-header') as HTMLElement | null;

  if (alertSection) {
    remove(alertSection);
  }

  const flashHTML = createElementFromHTML(
    `<div id='alert-flash' class='${classes}'>${text}</div>`,
  );
  if (ewHeader) {
    insertAfter(flashHTML, ewHeader);
  }
}

/**
 * Subscribes an email to store-specific events.
 * @param email The email to subscribe.
 * @returns A promise that resolves when the subscription is processed.
 */
export async function sendSubscribeEventToStore(email: string): Promise<void> {
  const storeURL = `${process.env.GATSBY_STORE_BASE_URL}/aapi/v3/notification_events`;
  const storeTokenAuth = `Basic ${process.env.GATSBY_STORE_BASIC_AUTH_TOKEN}`;

  const eventPayload = {
    event_type: 'emailCollectionBlog',
    resource: {
      email,
      data_fields: { emailCollectionBlog: email },
    },
  };

  try {
    await fetch(storeURL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: storeTokenAuth,
        'Content-Type': 'application/json',
        'EW-Event-Source': 'blog',
      },
      body: JSON.stringify(eventPayload),
    });
    showFlash('alert-success', 'Thanks for subscribing!');
  } catch (error) {
    const errorMsg = error instanceof Error ? error.toString() : String(error);
    showFlash(
      'alert-error',
      'Unable to subscribe, please try an alternate email.',
    );
    logError('Subscription error', { error: errorMsg, email });
  }
}

/**
 * Logs an error to Datadog.
 * @param errorMessage The error message.
 * @param context Additional context information.
 */
const logError = (
  errorMessage: string,
  context: Record<string, unknown>,
): void => {
  datadogLogs.logger.error(errorMessage, context);
};

export { logError };

/**
 * Checks if a URL is external.
 * @param url The URL to check.
 * @returns True if the URL starts with "http".
 */
export const isExternalUrl = (url: string | undefined | null): boolean =>
  (url ?? '').startsWith('http');

/**
 * Returns an absolute URL.
 * @param url The URL to convert.
 * @returns An absolute URL, prepending the wwwURL if needed.
 */
export const ensureAbsoluteUrl = (url: string | undefined | null): string =>
  isExternalUrl(url) ? url! : `${wwwURL}${url ?? ''}`;
